import { useEffect, useState } from "react";

export interface CookieConsent {
  essential: boolean;
  analytics: boolean;
  functional: boolean;
  marketing: boolean;
}

export function useCookieConsent() {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Load consent from localStorage
    const loadConsent = () => {
      const savedConsent = localStorage.getItem("cookie_consent");
      if (savedConsent) {
        try {
          const parsed = JSON.parse(savedConsent);
          setConsent(parsed);
          setShowBanner(false);
        } catch (error) {
          console.error("Error parsing saved cookie consent:", error);
          setConsent({
            essential: true,
            analytics: false,
            functional: false,
            marketing: false,
          });
          setShowBanner(true);
        }
      } else {
        // Default to essential only
        setShowBanner(true);
        setConsent({
          essential: true,
          analytics: false,
          functional: false,
          marketing: false,
        });
      }
      setIsLoaded(true);
    };

    loadConsent();

    // Listen for storage changes (when consent is updated from another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "cookie_consent") {
        loadConsent();
      }
    };

    // Listen for custom event (when consent is updated in same tab)
    const handleConsentUpdate = () => {
      loadConsent();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("cookieConsentUpdated", handleConsentUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("cookieConsentUpdated", handleConsentUpdate);
    };
  }, []);

  const updateConsent = (newConsent: CookieConsent) => {
    setConsent(newConsent);
    setShowBanner(false);
    localStorage.setItem("cookie_consent", JSON.stringify(newConsent));

    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent("cookieConsentUpdated"));

    // Trigger analytics based on consent
    if (typeof window !== "undefined") {
      if (window.gtag) {
        if (newConsent.analytics) {
          window.gtag("consent", "update", {
            analytics_storage: "granted",
          });
        } else {
          window.gtag("consent", "update", {
            analytics_storage: "denied",
          });
        }

        if (newConsent.marketing) {
          window.gtag("consent", "update", {
            ad_storage: "granted",
            personalization_storage: "granted",
          });
        } else {
          window.gtag("consent", "update", {
            ad_storage: "denied",
            personalization_storage: "denied",
          });
        }

        if (newConsent.functional) {
          window.gtag("consent", "update", {
            functionality_storage: "granted",
          });
        } else {
          window.gtag("consent", "update", {
            functionality_storage: "denied",
          });
        }
      }

      // Load Google Analytics if consent is granted and not already loaded
      if (
        newConsent.analytics &&
        window.loadGoogleAnalytics &&
        !document.querySelector('script[src*="googletagmanager.com"]')
      ) {
        window.loadGoogleAnalytics();
      }
    }
  };

  const hasConsent = (category: keyof CookieConsent): boolean => {
    return consent?.[category] ?? false;
  };

  const canLoadAnalytics = (): boolean => {
    return hasConsent("analytics");
  };

  const canLoadMarketing = (): boolean => {
    return hasConsent("marketing");
  };

  const canLoadFunctional = (): boolean => {
    return hasConsent("functional");
  };

  const handleAcceptAll = () => {
    const consent: CookieConsent = {
      essential: true,
      analytics: true,
      functional: true,
      marketing: true,
    };

    localStorage.setItem("cookie_consent", JSON.stringify(consent));
    setShowBanner(false);
    updateConsent(consent);
  };

  const handleRejectNonEssential = () => {
    const consent: CookieConsent = {
      essential: true,
      analytics: false,
      functional: false,
      marketing: false,
    };

    localStorage.setItem("cookie_consent", JSON.stringify(consent));
    setShowBanner(false);
    updateConsent(consent);
  };

  return {
    consent,
    isLoaded,
    updateConsent,
    hasConsent,
    canLoadAnalytics,
    canLoadMarketing,
    canLoadFunctional,
    showBanner,
    setShowBanner,
    handleAcceptAll,
    handleRejectNonEssential,
  };
}

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    loadGoogleAnalytics: () => void;
  }
}
